<template>
  <a-modal
    :visible="visible"
    width="820px"
    :body-style="{ minHeight: '700px' }"
    :keyboard="false"
    centered
    :mask-closable="false"
    destroyOnClose
    @cancel="handleCloseConfirm"
    title="Solicitar Plantilla"
  >
    <CustomStepper :value="currentStep" @update:value="current = $event" :items="steps" readonly />

    <section class="steps--container">
      <keep-alive>
        <component
          :key="currentComponent"
          :is="currentComponent"
          :templatesData="templatesData"
          :associate="associate"
          ref="currentStepRef"
          v-bind="parametersProps"
          @active-button="handleActiveButton"
          @inactive-button="handleInactiveButton"
          @clear-content="handleClearContent"
        />
      </keep-alive>
    </section>

    <template #footer>
      <a-button @click="handleSecondaryAction"> {{ secondaryButtonText }} </a-button>

      <a-button type="primary" :disabled="isButtonDisabled" @click="handlePrimaryAction">
        {{ primaryButtonText }}
      </a-button>
    </template>
  </a-modal>
</template>

<script>
import { computed, createVNode, defineComponent, reactive, ref, watch, provide, nextTick } from "vue";
import { message, Modal } from "ant-design-vue";
import { QuestionCircleOutlined } from "@ant-design/icons-vue";
import { useRoute } from "vue-router";

import { templateParamRegex } from "@/app/channels/utils/regex";
import { isContentConsecutive } from "@/app/shared/utils/helpers";
import { requestTemplate } from "@/app/channels/services";
import { WhatsappTemplateCategories, WhatsappTemplateTypeCall, WhatsappTemplateTypeSpecificButtons } from "@/app/channels/utils/enums";
import { CompanyName } from "@/app/shared/utils/constants";

import CustomStepper from "@/app/shared/components/organisms/CustomStepper.vue";
import NameStep from "./steps/NameStep.vue";
import LanguageStep from "./steps/LanguageStep.vue";
import ButtonsStep from "./steps/ButtonsStep.vue";
import ContentStep from "./steps/ContentStep.vue";
import ParametersStep from "./steps/ParametersStep.vue";

// const components = ["Name", "Language", "Content", "Parameters", "Buttons"];
const CANCEL_ACTION_TEXT = "Cancelar";
const BACK_STEP_ACTION_TEXT = "Volver";
const NEXT_STEP_ACTION_TEXT = "Continuar";
const CREATE_ACTION_TEXT = "Solicitar";

export default defineComponent({
  components: {
    CustomStepper,
    NameStep,
    LanguageStep,
    ContentStep,
    ParametersStep,
    ButtonsStep,
  },
  props: {
    visible: {
      required: true,
      type: Boolean,
      default: false,
    },
    template: {
      required: false,
      type: Object,
    },
    templatesData: {
      required: true,
      type: Array,
    },
    channelCustomerId: {
      required: true,
      type: Number,
    },
  },
  setup(props, { emit }) {
    const route = useRoute();

    const steps = reactive(["Nombre", "Configuración", "Contenido", "Parámetros"]);

    const currentStep = ref(0);
    const currentStepRef = ref(null);
    const secondaryButtonText = ref(CANCEL_ACTION_TEXT);
    const primaryButtonText = ref(NEXT_STEP_ACTION_TEXT);

    const templateType = ref(null);
    const templateButton = ref(null);

    const formStateContent = reactive({
      content: "",
    });

    provide("templateType", templateType);
    provide("templateButton", templateButton);
    provide("steps", steps);
    provide("formStateContent", formStateContent);

    const formatParamNumber = (idx) => `{{${idx + 1}}}`;

    const associate = computed(() => props.templatesData[0]?.channel_customer?.associate);
    const currentComponent = computed(() => {
      switch (steps[currentStep.value]) {
        case "Nombre":
          return "NameStep";

        case "Configuración":
          return LanguageStep;

        case "Contenido":
          return ContentStep;

        case "Parámetros":
          return ParametersStep;

        case "Botones":
          return ButtonsStep;

        default:
          return "NameStep";
      }
    });

    const getIndexByStepName = (step) => steps.findIndex((s) => s === step);

    watch(currentStep, async (stepValue) => {
      if (stepValue === 0) secondaryButtonText.value = CANCEL_ACTION_TEXT;
      if (stepValue > 0) secondaryButtonText.value = BACK_STEP_ACTION_TEXT;
      if (stepValue === steps.length - 1) primaryButtonText.value = CREATE_ACTION_TEXT;
      if (stepValue < steps.length - 1) primaryButtonText.value = NEXT_STEP_ACTION_TEXT;
      await nextTick();
      if (currentStepRef.value?.validateFormState) currentStepRef.value?.validateFormState({ validateEmpty: false });
      if (currentStepRef.value.valid) handleActiveButton();

    });

    const handleSecondaryAction = () => {
      const parametersInContent = formState.content?.match(templateParamRegex);
      const isParamsStep = currentStep.value === getIndexByStepName("Parámetros");

      if (formState.category === WhatsappTemplateCategories.OTP && isParamsStep) {
        currentStep.value -= 1;
        handleActiveButton();
        return;
      }

      if (
        !parametersInContent &&
        steps.includes("Botones") &&
        currentStep.value === steps.length - 1
      ) {
        currentStep.value -= 2;
        return handleActiveButton();
      }
      if (currentStep.value > 0) {
        currentStep.value--;
        return handleActiveButton();
      }

      return handleClose();
    };

    const isButtonDisabled = ref(true);
    let formState = reactive({});

    const parametersProps = reactive({
      number: null,
      content: null,
      category: null,
    });

    const handlePrimaryAction = () => {
      formState = { ...formState, ...currentStepRef.value.formState };
      parametersProps.category = formState.category;

      const isContentStep = currentStep.value === getIndexByStepName("Contenido");
      const isConfigStep = currentStep.value === getIndexByStepName("Configuración");

      if (isConfigStep && formState.category === WhatsappTemplateCategories.OTP) {
        const defaultAuthenticationTemplate = "Tu código de verificación es {{1}}";

        parametersProps.number = 1;
        parametersProps.content = defaultAuthenticationTemplate;
        formState.content = defaultAuthenticationTemplate;

        currentStep.value += 1;
        return;
      }

      if (formState.content && isContentStep) {
        const parametersInContent = formState.content?.match(templateParamRegex);

        if (!parametersInContent && steps.includes("Botones")) {
          currentStep.value += 2;
          parametersProps.content = formState.content;
          return;
        }

        if (!parametersInContent && !steps.includes("Botones")) {
          handleRequest();
          return;
        }

        const numbersInParameters = parametersInContent
          .flatMap((param) => param.match(/[0-9]+/g))
          .map((num) => Number(num));

        const isValidContent =
          numbersInParameters?.[0] === 1 && isContentConsecutive(numbersInParameters);

        if (!isValidContent) {
          message.error(
            "Los parámetros deben comenzar con el número 1, ser consecutivos y no repetirse"
          );
          return;
        }

        parametersProps.number = parametersInContent.length;
        parametersProps.content = formState.content;
      }

      if (currentStep.value < steps.length - 1 ) {
        currentStep.value++;
        handleInactiveButton();
        return;
      }

      return handleRequest();
    };

    const propsChannelCustomerId = computed(() => props.channelCustomerId);
    const handleRequest = async () => {
      const channelCustomerId = Number(
        propsChannelCustomerId.value ?? route.params.channel_customer
      );
      const { content, parameters: unformattedParams, ...templateData } = formState;

      console.log(formState, "FORMSTATE");

      const body = {
        ...templateData,
        template: content,
        template_type: templateType.value,
        channel_customer: channelCustomerId,
      };

      if (body.buttons) {
        body.buttons = body.buttons.map((b) => ({
          ...b,
          type:
            b.callOutput === WhatsappTemplateTypeCall.WhatsappVoice
              ? WhatsappTemplateTypeSpecificButtons.VoiceCall
              : b.type
        }));
      }

      if (unformattedParams) {
        const formattedParams = Object.values(unformattedParams).map((paramName) => ({
          name: paramName,
        }));

        body.parameters = formattedParams;
      }

      if (body.category === WhatsappTemplateCategories.OTP) {
        body.buttons = [{ type: "OTP", otp_type: "COPY_CODE", text: "Copiar código" }];
      }

      if (
        (steps.includes("Botones") && currentStep.value === getIndexByStepName("Botones")) ||
        currentStep.value === getIndexByStepName("Contenido") ||
        currentStep.value === getIndexByStepName("Parámetros")
      ) {
        Modal.confirm({
          icon: createVNode(QuestionCircleOutlined),
          title: `¿Estás seguro de solicitar esta nueva plantilla a ${CompanyName}?`,
          content:
            "Recuerda que una vez solicitada la plantilla, no podrás suspender el proceso ni editarla posteriormente, a excepción del nombre de visualización.",
          okText: "Solicitar",
          cancelText: "Volver",
          onOk: async () => {
            try {
              await requestTemplate({ channelCustomerId, body });
              emit("update", channelCustomerId);
              message.success("Se ha solicitado la plantilla");
            } catch (error) {
              const errorMessage = error?.getErrorMessage() || "Ha courrido un error";

              message.error(errorMessage);
              console.log(error);
            }
            handleClose();
          },
        });
      }
    };

    const handleClose = () => emit("close");

    const handleActiveButton = () => {
      isButtonDisabled.value = false;
    };

    const handleInactiveButton = () => {
      isButtonDisabled.value = true;
    };

    const handleClearContent = () => {
      formStateContent.content = "";
    };

    const handleCloseConfirm = () => {
      Modal.confirm({
        title: "¿Deseas descartar los cambios realizados?",
        content: "Recuerda que si no guardas los cambios registrados, estos se perderán.",
        icon: createVNode(QuestionCircleOutlined),
        onOk: () => handleClose(),
        centered: true,
        okText: "Sí, descartar cambios",
        cancelText: "No, seguir editando",
      });
    };

    return {
      steps,
      currentStep,
      currentStepRef,
      secondaryButtonText,
      primaryButtonText,
      associate,
      currentComponent,
      formatParamNumber,
      isButtonDisabled,
      parametersProps,
      handleSecondaryAction,
      handlePrimaryAction,
      handleClose,
      handleActiveButton,
      handleInactiveButton,
      propsChannelCustomerId,
      handleClearContent,
      handleCloseConfirm,
    };
  },
});
</script>

<style lang="sass" scoped>
.steps
  &--container
    margin: 24px 60px
    height: calc(100% - 100px)
</style>
